import React, { useEffect, useState } from "react";
import { expenseService } from "../../services/expenseService";
import { vendorService } from "../../services/vendorService";
import toast from "react-hot-toast";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";
import "../../styles/components/expenses/ExpenseReport.css";

const ExpenseReport = ({ refreshFlag }) => {
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });

  // Fetch all expenses
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await expenseService.getAllExpenses();
      if (response.data.success) {
        const numericExpenses = response.data.expenses.map(item => ({
          ...item,
          amount: Number(item.amount),
          expense_date: item.expense_date
        }));
        setExpenses(numericExpenses);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.log("Error fetching expenses:", error);
      toast.error("Failed to load expenses");
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendors
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await vendorService.getAllVendors();
        if (res.data.success) {
          setVendors(res.data.vendors);
        }
      } catch (error) {
        toast.error("Failed to load vendors");
        console.error("Vendor fetch error:", error);
      }
    };
    loadVendors();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [refreshFlag]);

  // Filtering (Category + Vendor + Date Range)
  const filteredExpenses = expenses.filter(exp => {
    const expDate = new Date(exp.expense_date);
    const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
    const toDate = dateFilter.to ? new Date(dateFilter.to) : null;

    const matchesCategory = !categoryFilter || exp.category === categoryFilter;
    const matchesVendor = !vendorFilter || exp.vendor_id == vendorFilter;
    const matchesSupplier = !supplierFilter || exp.supplier === supplierFilter;
    const matchesFrom = !fromDate || expDate >= fromDate;
    const matchesTo = !toDate || expDate <= toDate;

    return matchesCategory && matchesVendor && matchesSupplier && matchesFrom && matchesTo;
  });

  // Extract unique suppliers from expenses
  const uniqueSuppliers = [...new Set(expenses.map(exp => exp.supplier).filter(Boolean))].sort();

  // Trigger toast when no records found after filtering
  useEffect(() => {
    if (!loading && !filteredExpenses.length) {
      toast.error("No expenses found for selected filters");
    }
  }, [filteredExpenses, loading]);

  // Summary calculation (only filtered expenses)
  const summary = filteredExpenses.reduce((acc, exp) => {
    const existing = acc.find(item => item.category === exp.category);
    if (existing) {
      existing.total_amount += exp.amount;
    } else {
      acc.push({ category: exp.category, total_amount: exp.amount });
    }
    return acc;
  }, []);

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const maxAmount = Math.max(...summary.map(item => item.total_amount), 0);
  const COLORS = ["#4d70ff", "#ff7676", "#ffb74d", "#66bb6a", "#9575cd", "#26c6da"];

  if (loading) return <div className="report-loading">Loading report...</div>;

  return (
    <div className="expense-report-container">
      <h2 className="report-title">Expense Report</h2>

      {/* Filters */}
      <div className="expense-filters">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          <option value="Food">Food</option>
          <option value="Fuel">Fuel</option>
          <option value="Supplies">Supplies</option>
          <option value="Maintenance">Maintenance</option>
          <option value="Utilities">Utilities</option>
          <option value="Misc">Misc</option>
        </select>

        <input
          type="date"
          value={dateFilter.from}
          onChange={e => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
        />
        <input
          type="date"
          value={dateFilter.to}
          onChange={e => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
        />

        <select
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
        >
          <option value="">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>

        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
        >
          <option value="">All Suppliers</option>
          {uniqueSuppliers.map((s, idx) => (
            <option key={idx} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Total Expenses */}
      <div className="total-expenses">
        <h3>Total Expenses: ₦{totalExpenses.toLocaleString()}</h3>
      </div>

      {/* Summary Cards */}
      {filteredExpenses.length ? (
        <div className="summary-card-wrapper">
          {summary.map((item, index) => (
            <div
              key={index}
              className="summary-card"
              style={{
                border: item.total_amount === maxAmount ? "2px solid #ff7676" : "1px solid #ccc"
              }}
            >
              <h3>{item.category}</h3>
              <p>₦{Number(item.total_amount).toLocaleString()}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-report">No expenses to display</div>
      )}

      {/* Charts */}
      <div className="chart-section">
        {filteredExpenses.length ? (
          <>
            {/* PIE CHART */}
            <div className="chart-card">
              <h3 className="chart-title">Expense Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={summary}
                    dataKey="total_amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    label
                  >
                    {summary.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={COLORS[index % COLORS.length]}
                        stroke={entry.total_amount === maxAmount ? "#ff7676" : ""}
                        strokeWidth={entry.total_amount === maxAmount ? 4 : 0}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* BAR CHART */}
            <div className="chart-card">
              <h3 className="chart-title">Category Comparison</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summary}>
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="total_amount"
                    fill="#4d70ff"
                    radius={[6, 6, 0, 0]}
                    label={{ position: "top" }}
                  >
                    {summary.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={entry.total_amount === maxAmount ? "#ff7676" : "#4d70ff"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <p style={{ textAlign: "center", padding: "2rem" }}>No data available for charts</p>
        )}
      </div>
    </div>
  );
};

export default ExpenseReport;
